import { buildCallerId } from '@b2ag/utils/src/caller-id'
import { i18n } from '@b2ag/locale'

import { ShippingMethodKind, ShippingMethodShortTitles, ShippingMethodTitles, Stock } from './stock.business'
import type { InvivoLocale } from '@invivodf/locale'
import { createStockSdk } from '@b2ag/utils/src/esmParser'
import { Stock2, StockReadErrorSdk, StockSDK } from '@invivodf/module-stock-sdk'
import type { AxiosInstance } from '@b2ag/axios'
import { createAxiosClientWithAladinBearer } from '@b2ag/axios'

export interface IStockService {
  search(
    cooperativeId: string,
    customerId: string,
    variantIds: string[],
    isSearchStockV2Enabled: boolean,
  ): Promise<Stock[] | undefined>

  get(cooperativeId: string, variantId: string): Promise<any>
}

export class StockService implements IStockService {
  api: AxiosInstance
  stockSdk: StockSDK

  constructor(apiHost: string, accessTokenSource: () => Promise<string | null>) {
    this.stockSdk = createStockSdk(apiHost, {
      accessTokenSource,
      callerId: buildCallerId(),
      localeSource: () => i18n.locale as InvivoLocale,
    })
    this.api = createAxiosClientWithAladinBearer(`${apiHost}/v1/stocks`, { callerId: buildCallerId(), i18n })
  }

  async get(cooperativeId, variantId) {
    const params = {
      cooperative_id: cooperativeId,
    }
    const response = await this.api.get(`/${variantId}`, {
      params,
      validateStatus(status) {
        return status === 200 || status === 404
      },
    })
    return response.status === 404 ? null : response.data
  }

  async search(
    cooperativeId: string | number,
    customerId: string,
    variantIds: string[],
    isSearchStockV2Enabled: boolean,
  ): Promise<Stock[] | undefined> {
    if (isSearchStockV2Enabled) {
      const stocks = await this.stockSdk.searchStocks(cooperativeId.toString(), customerId, variantIds)
      if (stocks instanceof StockReadErrorSdk) return undefined

      return stocks.map((stock) => this.mapToStock(stock))
    }
  }

  private mapToStock(stock: Stock2): Stock {
    return {
      click_and_collect: stock.click_and_collect,
      quantity: stock.quantity,
      variant_id: stock.variant_id,
      stores: stock.stores.map((store) => ({
        code: store.code,
        quantity: store.quantity,
        is_click_and_collect: store.is_click_and_collect,
        is_shipping: store.is_shipping,
        shipping_methods: store.shipping_methods.map((shippingMethod) => ({
          kind: shippingMethod.kind === 'shipping' ? ShippingMethodKind.Farm : ShippingMethodKind.ClickAndCollect,
          short_title:
            shippingMethod.kind === 'shipping'
              ? ShippingMethodShortTitles.Farm
              : ShippingMethodShortTitles.ClickAndCollect,
          title: shippingMethod.kind === 'shipping' ? ShippingMethodTitles.Farm : ShippingMethodTitles.ClickAndCollect,
        })),
      })),
    }
  }
}
